import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { getDepartmentsAndTaxRates, DepartmentVatInfo } from '../../hooks/fiscalDevices/ingenico';

interface IngenicoDeviceDepartmentsModalProps {
  open: boolean;
  onClose: () => void;
}

const IngenicoDeviceDepartmentsModal: React.FC<IngenicoDeviceDepartmentsModalProps> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState<DepartmentVatInfo[]>([]);
  const [taxRates, setTaxRates] = useState<{ index: number; percent: number }[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getDepartmentsAndTaxRates();
      if (result.success) {
        setDepartments(result.departments);
        setTaxRates(result.taxRates);
        setLoaded(true);
      } else {
        setError(result.error || 'Veri alınamadı');
      }
    } catch (err: any) {
      setError(err.message || 'Beklenmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !loaded) {
      fetchData();
    }
  }, [open]);

  const handleClose = () => {
    setLoaded(false);
    setDepartments([]);
    setTaxRates([]);
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: '#e3f2fd',
          borderBottom: '1px solid #bbdefb',
        }}
      >
        <DevicesIcon sx={{ color: '#1565c0', fontSize: 28 }} />
        <Typography component="span" variant="h6" fontWeight={600}>
          Ingenico Cihaz - Departman / KDV Tablosu
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ mr: 2 }} />
            <Typography>Cihazdan veriler okunuyor...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && loaded && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              KDV Oranları Tablosu
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {taxRates.map((t) => (
                <Chip
                  key={t.index}
                  label={`Index ${t.index}: %${t.percent}`}
                  size="small"
                  variant="outlined"
                  color={t.percent > 0 ? 'primary' : 'default'}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Departman - KDV Eşleşmesi ({departments.length} departman)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Dept No</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Departman Adı</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>KDV Index</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>KDV Oranı</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.map((dept) => (
                    <TableRow key={dept.deptIndex} hover>
                      <TableCell>
                        <Chip label={dept.deptIndex} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{dept.deptName}</TableCell>
                      <TableCell>{dept.taxIndex}</TableCell>
                      <TableCell>
                        <Chip
                          label={`%${dept.taxRatePercent}`}
                          size="small"
                          color={dept.taxRatePercent > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Yenile
        </Button>
        <Button
          variant="contained"
          startIcon={<CloseIcon />}
          onClick={handleClose}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IngenicoDeviceDepartmentsModal;
